import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { fetchBalanceSheet, fetchBalanceSheetTTM, BalanceSheet } from '../../services/stockApi';
import {
  fetchFiscalStandardizedBalanceSheet,
  fetchFiscalStandardizedBalanceSheetTtm,
  fetchFiscalStandardizedMetricsList,
  fetchFiscalBalanceSheetAsReportedFilingIds,
  fetchFiscalFilingPdfBlobUrl,
  fetchSecFilingUrlForYear,
  fetchSecFilingUrlForReportDate,
} from '../../services/fiscalApi';
import CenteredLoadingSpinner from '../../components/CenteredLoadingSpinner';
import DataSourceAttribution from '../../components/DataSourceAttribution';
import { adminApi, isUnauthorized } from '../../services/adminApi';
import type { SnackbarVariant } from '../../components/Snackbar';
import { RESEARCH_DESK_NOTE_CATEGORIES } from './researchDeskCategories';
import ResearchNoteModal from './ResearchNoteModal';
import FloatingNewNoteButton from './FloatingNewNoteButton';

interface ResearchBalanceSheetScreenProps {
  isDarkMode: boolean;
  onToggleTheme?: () => void;
  onBackToMain?: () => void;
  selectedStock: string;
  selectedPeriod: 'annual' | 'quarter';
  isAdmin?: boolean;
  authToken?: string | null;
  authUserId?: number | null;
  onUnauthorized?: () => void;
  onNotify?: (message: string, variant?: SnackbarVariant) => void;
}

const BALANCE_SHEET_FISCAL_TIMEOUT_MS = 5000;
const BALANCE_SHEET_FISCAL_CATALOG_TIMEOUT_MS = 5000;

function withBalanceSheetTimeout<T>(promise: Promise<T>, fallback: T, timeoutMs: number): Promise<T> {
  return new Promise((resolve) => {
    let settled = false;
    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve(fallback);
    }, timeoutMs);

    promise
      .then((value) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve(value);
      })
      .catch(() => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve(fallback);
      });
  });
}

const ResearchBalanceSheetScreen: React.FC<ResearchBalanceSheetScreenProps> = ({
  isDarkMode,
  selectedStock,
  selectedPeriod,
  isAdmin,
  authToken,
  authUserId,
  onUnauthorized,
  onNotify,
}) => {
  const [annualData, setAnnualData] = useState<BalanceSheet[]>([]);
  const [quarterlyData, setQuarterlyData] = useState<BalanceSheet[]>([]);
  const [ttmData, setTtmData] = useState<BalanceSheet | null>(null);
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState<'fmp' | 'fiscal'>('fmp');
  const [fiscalAnnualFilingIdByLabel, setFiscalAnnualFilingIdByLabel] = useState<Record<string, string>>({});
  const [fiscalQuarterFilingIdByLabel, setFiscalQuarterFilingIdByLabel] = useState<Record<string, string>>({});
  const loadRequestIdRef = useRef(0);

  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteCategory, setNoteCategory] = useState('Balance Sheet');
  const [noteContent, setNoteContent] = useState('');
  const NOTE_CATEGORIES = useMemo(() => [...RESEARCH_DESK_NOTE_CATEGORIES], []);

  const [showFloatingButton, setShowFloatingButton] = useState<boolean>(true);

  useEffect(() => {
    const update = () => setShowFloatingButton(window.innerWidth >= 1024);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const canPersist =
    !!authToken &&
    typeof authUserId === 'number' &&
    Number.isFinite(authUserId) &&
    (authUserId as number) > 0;
  const normalizeHtmlText = (raw: string) =>
    String(raw ?? '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const extractTitleFromContent = (html: string): string => {
    if (!html) return '';
    const blockRegex = /<(p|div|li|h1|h2|h3|h4|h5|h6)[^>]*>([\s\S]*?)<\/(p|div|li|h1|h2|h3|h4|h5|h6)>/gi;
    let match = blockRegex.exec(html);
    while (match) {
      const text = normalizeHtmlText(match[2]);
      if (text) return text.split(/\s+/).slice(0, 5).join(' ');
      match = blockRegex.exec(html);
    }
    const fallback = normalizeHtmlText(html);
    if (!fallback) return '';
    return fallback.split(/\s+/).slice(0, 5).join(' ');
  };

  const hasNoteText = normalizeHtmlText(noteContent).length > 0;
  const canAddNote = noteCategory.trim().length > 0 && hasNoteText && noteTitle.trim().length > 0;

  useEffect(() => {
    const derivedTitle = extractTitleFromContent(noteContent);
    setNoteTitle(derivedTitle);
  }, [noteContent]);

  const openNewNote = () => {
    setNoteCategory('Balance Sheet');
    setNoteTitle('');
    setNoteContent('');
    setIsNoteModalOpen(true);
  };

  const loadBalanceSheetData = React.useCallback(async () => {
    const requestId = ++loadRequestIdRef.current;
    setLoading(true);
    try {
      const [annual, quarterly, ttm] = await Promise.all([
        fetchBalanceSheet(selectedStock, 'annual', 15),
        fetchBalanceSheet(selectedStock, 'quarter', 15),
        fetchBalanceSheetTTM(selectedStock, 1),
      ]);

      if (requestId !== loadRequestIdRef.current) return;

      setAnnualData(annual);
      setQuarterlyData(quarterly);
      setTtmData(ttm);
      setDataSource('fmp');
      setLoading(false);

      const metrics = await withBalanceSheetTimeout(
        fetchFiscalStandardizedMetricsList(),
        [],
        BALANCE_SHEET_FISCAL_CATALOG_TIMEOUT_MS
      );
      if (requestId !== loadRequestIdRef.current) return;

      const [fiscalStatement, fiscalTtm] = await Promise.all([
        withBalanceSheetTimeout(
          fetchFiscalStandardizedBalanceSheet(selectedStock, metrics),
          { annual: [], quarter: [], error: 'Fiscal balance sheet timed out.' },
          BALANCE_SHEET_FISCAL_TIMEOUT_MS
        ),
        withBalanceSheetTimeout(
          fetchFiscalStandardizedBalanceSheetTtm(selectedStock, metrics),
          { row: null, error: 'Fiscal balance sheet TTM timed out.' },
          BALANCE_SHEET_FISCAL_TIMEOUT_MS
        ),
      ]);

      if (requestId !== loadRequestIdRef.current) return;

      const fiscalCount = fiscalStatement.annual.length + fiscalStatement.quarter.length;
      const errorText = String(fiscalStatement.error || fiscalTtm.error || '').trim();
      const unavailableByMessage =
        Boolean(errorText) && /data is not yet available|companies-list|companies list/i.test(errorText);
      const fiscalUnavailable = unavailableByMessage || fiscalCount === 0;

      if (!fiscalUnavailable) {
        const mergeByDate = (fiscalRows: BalanceSheet[], fmpRows: BalanceSheet[]) => {
          const byDate = new Map<string, BalanceSheet>();
          for (const row of fmpRows) {
            if (row?.date) byDate.set(String(row.date).slice(0, 10), row);
          }
          for (const row of fiscalRows) {
            if (row?.date) byDate.set(String(row.date).slice(0, 10), row);
          }
          return Array.from(byDate.values()).sort((a, b) => Date.parse(String(a.date)) - Date.parse(String(b.date)));
        };

        setAnnualData(mergeByDate(fiscalStatement.annual, annual));
        setQuarterlyData(mergeByDate(fiscalStatement.quarter, quarterly));
        setTtmData(fiscalTtm.row);
        setDataSource('fiscal');
      }
    } catch (error) {
      if (requestId !== loadRequestIdRef.current) return;
      console.error('Error loading balance sheet:', error);
      setLoading(false);
    }
  }, [selectedStock]);

  useEffect(() => {
    loadBalanceSheetData();
  }, [loadBalanceSheetData]);

  useEffect(() => {
    let cancelled = false;

    const formatLabel = (dateStr: string): string => {
      const date = new Date(dateStr);
      if (Number.isNaN(date.getTime())) return dateStr;
      const month = date.toLocaleString('en-US', { month: 'short' });
      const year = date.getFullYear().toString().slice(-2);
      return `${month} ${year}`;
    };

    const buildLabelMap = (rows: Array<{ filingId?: string; date?: string }>) => {
      const map: Record<string, string> = {};
      rows.forEach((row) => {
        const id = String(row.filingId ?? '').trim();
        const date = String(row.date ?? '').trim();
        if (!id || !date) return;
        const label = formatLabel(date);
        if (!map[label]) map[label] = id;
      });
      return map;
    };

    (async () => {
      try {
        const result = await fetchFiscalBalanceSheetAsReportedFilingIds(selectedStock);
        if (cancelled) return;
        setFiscalAnnualFilingIdByLabel(buildLabelMap(result.annual ?? []));
        setFiscalQuarterFilingIdByLabel(buildLabelMap(result.quarter ?? []));
      } catch {
        if (!cancelled) {
          setFiscalAnnualFilingIdByLabel({});
          setFiscalQuarterFilingIdByLabel({});
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedStock]);

  const openFiscalFilingPdfById = useCallback(
    async (filingId?: string) => {
      const safeId = String(filingId ?? '').trim();
      if (!safeId) {
        onNotify?.('Missing filing ID for this year.', 'error');
        return;
      }

      const blobUrl = await fetchFiscalFilingPdfBlobUrl(selectedStock, safeId);
      if (!blobUrl) {
        onNotify?.('Unable to load filing PDF. Please login and try again.', 'error');
        return;
      }

      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    },
    [onNotify, selectedStock]
  );

  const openAnnualFilingForYear = useCallback(
    async (year: number, fallbackId?: string) => {
      if (!Number.isFinite(year)) {
        onNotify?.('Missing filing year.', 'error');
        return;
      }

      const secUrl = await fetchSecFilingUrlForYear(selectedStock, year, '10-K');
      if (secUrl) {
        window.open(secUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      if (fallbackId) {
        await openFiscalFilingPdfById(fallbackId);
        return;
      }

      onNotify?.('Unable to load annual filing for this year.', 'error');
    },
    [onNotify, openFiscalFilingPdfById, selectedStock]
  );

  const openQuarterFilingForDate = useCallback(
    async (reportDate: string, fallbackId?: string, form: string = '10-Q') => {
      const rawDate = String(reportDate ?? '').trim();
      if (!rawDate) {
        onNotify?.('Missing filing date.', 'error');
        return;
      }

      const normalizedDate = (() => {
        const match = rawDate.match(/^\d{4}-\d{2}-\d{2}/);
        if (match) return match[0];
        const parsed = new Date(rawDate);
        if (Number.isNaN(parsed.getTime())) return rawDate;
        return parsed.toISOString().slice(0, 10);
      })();

      const secUrl = await fetchSecFilingUrlForReportDate(selectedStock, normalizedDate, form);
      if (secUrl) {
        window.open(secUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      if (fallbackId) {
        await openFiscalFilingPdfById(fallbackId);
        return;
      }

      onNotify?.('Unable to load quarterly filing for this date.', 'error');
    },
    [onNotify, openFiscalFilingPdfById, selectedStock]
  );

  const formatNumber = (value: number): string => {
    if (!Number.isFinite(value)) return '-';
    if (Math.abs(value) >= 1e9) {
      return (value / 1e9).toFixed(2) + 'B';
    } else if (Math.abs(value) >= 1e6) {
      return (value / 1e6).toFixed(2) + 'M';
    }
    return value.toFixed(2);
  };

  const withNegativeColor = (base: React.CSSProperties, renderedText: string): React.CSSProperties => {
    if (typeof renderedText !== 'string') return base;
    const trimmed = renderedText.trim();
    if (trimmed.startsWith('-')) return { ...base, color: '#F44336' };
    return base;
  };

  const formatDate = (dateStr: string, period: 'annual' | 'quarter'): string => {
    const date = new Date(dateStr);
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2);

    // Use the actual month from the statement date (matches Income Statement behavior).
    // This correctly shows fiscal-year ends like Jun for MSFT.
    return `${month} ${year}`;
  };

  const annualSortedDesc = [...annualData]
    .filter((d) => d?.date)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const quarterlySortedDesc = [...quarterlyData]
    .filter((d) => d?.date)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const annualPeriods = annualSortedDesc.slice(0, 10).reverse();
  const quarterlyPeriods = quarterlySortedDesc.slice(0, 5).reverse();

  const annualDateSet = useMemo(() => {
    const set = new Set<string>();
    annualPeriods.forEach((row) => {
      if (row?.date) set.add(row.date);
    });
    return set;
  }, [annualPeriods]);

  const latestAnnualYear = useMemo(() => {
    if (annualPeriods.length === 0) return null;
    const last = annualPeriods[annualPeriods.length - 1]?.date;
    if (!last) return null;
    const y = new Date(last).getFullYear();
    return Number.isFinite(y) ? y : null;
  }, [annualPeriods]);

  const latestAnnualDateKey = useMemo(() => {
    if (annualPeriods.length === 0) return null;
    return annualPeriods[annualPeriods.length - 1]?.date ?? null;
  }, [annualPeriods]);

  const latestQuarterDateForAnnualYear = useMemo(() => {
    if (latestAnnualYear == null) return null;
    const matches = quarterlyPeriods.filter((q) => {
      const y = new Date(q.date).getFullYear();
      return Number.isFinite(y) && y === latestAnnualYear;
    });
    if (matches.length === 0) return null;
    return matches[matches.length - 1]?.date ?? null;
  }, [latestAnnualYear, quarterlyPeriods]);

  const getHighlightedQuarterDate = useCallback(
    (quarters: Array<{ date: string }>) => {
      if (latestAnnualDateKey) {
        const match = quarters.find((q) => q.date === latestAnnualDateKey);
        if (match) return latestAnnualDateKey;
      }
      return latestQuarterDateForAnnualYear;
    },
    [latestAnnualDateKey, latestQuarterDateForAnnualYear]
  );

  const highlightedQuarterDate = useMemo(
    () => getHighlightedQuarterDate(quarterlyPeriods),
    [getHighlightedQuarterDate, quarterlyPeriods]
  );

  type Row = {
    label: string;
    getValue: (bs: BalanceSheet) => string;
    isBold?: boolean;
    isSpacer?: boolean;
  };

  const baseRows: Row[] = [
    { label: 'Cash & Equivalents', getValue: (bs) => formatNumber(bs.cashAndCashEquivalents || 0) },
    { label: 'Short Term Investments', getValue: (bs) => formatNumber(bs.shortTermInvestments || 0) },
    { label: 'Cash and Short Term Investments', getValue: (bs) => formatNumber(bs.cashAndShortTermInvestments || 0) },
    { label: 'Receivables', getValue: (bs) => formatNumber(bs.netReceivables || 0) },
    { label: 'Inventory', getValue: (bs) => formatNumber(bs.inventory || 0) },
    { label: 'Other Current Assets', getValue: (bs) => formatNumber(bs.otherCurrentAssets || 0) },
    { label: 'Total Current Assets', getValue: (bs) => formatNumber(bs.totalCurrentAssets || 0), isBold: true },
    { label: 'Property, Plant and Equipment', getValue: (bs) => formatNumber(bs.propertyPlantEquipmentNet || 0) },
    { label: 'Goodwill', getValue: (bs) => formatNumber(bs.goodwill || 0) },
    { label: 'Intangible Assets', getValue: (bs) => formatNumber(bs.intangibleAssets || 0) },
    { label: 'Long Term Investments', getValue: (bs) => formatNumber(bs.longTermInvestments || 0) },
    { label: 'Deferred Tax Assets', getValue: (bs) => formatNumber(bs.taxAssets || 0) },
    { label: 'Other Long Term Assets', getValue: (bs) => formatNumber(bs.otherNonCurrentAssets || 0) },
    { label: 'Total Long-Term Assets', getValue: (bs) => formatNumber(bs.totalNonCurrentAssets || 0), isBold: true },
    { label: 'Total Assets', getValue: (bs) => formatNumber(bs.totalAssets || 0), isBold: true },

    { label: 'Accounts Payable', getValue: (bs) => formatNumber(bs.accountPayables || 0) },
    { label: 'Other Payables', getValue: (bs) => formatNumber((bs as any).otherPayables || 0) },
    { label: 'Accrued Expenses', getValue: (bs) => formatNumber((bs as any).accruedExpenses || 0) },
    { label: 'Short Term Debt', getValue: (bs) => formatNumber(bs.shortTermDebt || 0) },
    { label: 'Tax Payables', getValue: (bs) => formatNumber(bs.taxPayables || 0) },
    { label: 'Deferred Revenue', getValue: (bs) => formatNumber(bs.deferredRevenue || 0) },
    { label: 'Other Current Liabilities', getValue: (bs) => formatNumber(bs.otherCurrentLiabilities || 0) },
    { label: 'Total Current Liabilities', getValue: (bs) => formatNumber(bs.totalCurrentLiabilities || 0), isBold: true },
    { label: 'Total Long Term Debt', getValue: (bs) => formatNumber(bs.longTermDebt || 0) },
    {
      label: 'Capital Lease Obligations',
      getValue: (bs) =>
        formatNumber(((bs as any).capitalLeaseObligationsNonCurrent ?? bs.capitalLeaseObligations ?? 0) as number),
    },
    { label: 'Deferred Revenue Non-Current', getValue: (bs) => formatNumber(bs.deferredRevenueNonCurrent || 0) },
    {
      label: 'Deferred Tax Liabilities Non-Current',
      getValue: (bs) => formatNumber(bs.deferredTaxLiabilitiesNonCurrent || 0),
    },
    { label: 'Other Long Term Liabilities', getValue: (bs) => formatNumber(bs.otherNonCurrentLiabilities || 0) },
    { label: 'Total Long-Term Liabilities', getValue: (bs) => formatNumber(bs.totalNonCurrentLiabilities || 0), isBold: true },
    { label: 'Total Liabilities', getValue: (bs) => formatNumber(bs.totalLiabilities || 0), isBold: true },

    { label: 'Treasury Stock', getValue: (bs) => formatNumber((bs as any).treasuryStock || 0) },
    { label: 'Preferred Stock', getValue: (bs) => formatNumber(bs.preferredStock || 0) },
    { label: 'Common Stock', getValue: (bs) => formatNumber(bs.commonStock || 0) },
    { label: 'Retained Earnings', getValue: (bs) => formatNumber(bs.retainedEarnings || 0) },
    { label: 'Additional Paid In Capital', getValue: (bs) => formatNumber((bs as any).additionalPaidInCapital || 0) },
    {
      label: 'Accumulated Other Income (Loss)',
      getValue: (bs) => formatNumber(bs.accumulatedOtherComprehensiveIncomeLoss || 0),
    },
    {
      label: 'Other Stock Holders Equity',
      getValue: (bs) =>
        formatNumber((((bs as any).otherTotalStockholdersEquity ?? bs.othertotalStockholdersEquity) || 0) as number),
    },
    { label: 'Total Share Holders Equity', getValue: (bs) => formatNumber(bs.totalStockholdersEquity || 0), isBold: true },
    {
      label: 'Total Liabilities And Total Equity',
      getValue: (bs) => formatNumber(bs.totalLiabilitiesAndTotalEquity || 0),
      isBold: true,
    },
  ];

  const noSpacerAfterBoldLabels = new Set<string>([
    'Total Current Assets',
    'Total Long-Term Assets',
    'Total Current Liabilities',
    'Total Long-Term Liabilities',
    'Total Share Holders Equity',
    'Total Liabilities And Total Equity',
  ]);

  const rows: Row[] = baseRows
    .filter((row) => {
      if (dataSource !== 'fiscal') return true;
      return row.label !== 'Total Long-Term Assets';
    })
    .flatMap((row) =>
    row.isBold && !noSpacerAfterBoldLabels.has(row.label)
        ? [row, { label: '', getValue: () => '', isSpacer: true }]
        : [row]
    );

  const containerStyle: React.CSSProperties = {
    // Keep consistent spacing below Header `tabs-bar`.
    padding: '15px 2rem 2rem 2rem',
    minHeight: '100vh',
    backgroundColor: isDarkMode ? '#1a1a1a' : '#ffffff',
    color: isDarkMode ? '#ffffff' : '#000000'
  };

  const tableContainerStyle: React.CSSProperties = {
    width: '95.3%',
    marginLeft: '1rem',
    padding: '0 0.5rem 0.5rem 0.5rem',
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: '75vh',
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    borderRadius: '8px',
    border: `1px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`
  };

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  };

  const showSourceLabels = Boolean(isAdmin);
  const sourceLabelText = dataSource === 'fiscal' ? 'fiscal.ai' : 'FMP';
  const floatingSourceLabelStyle: React.CSSProperties = {
    position: 'absolute',
    right: '0.75rem',
    top: '0.35rem',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: isDarkMode ? '#9CA3AF' : '#6B7280',
    pointerEvents: 'none',
    zIndex: 8,
    padding: '0.1rem 0.35rem',
    background: isDarkMode ? '#111827' : '#ffffff',
    borderRadius: '6px',
    border: `1px solid ${isDarkMode ? '#1F2937' : '#e5e7eb'}`
  };

  const headerCellStyle: React.CSSProperties = {
    padding: '1rem',
    textAlign: 'center',
    borderBottom: `2px solid ${isDarkMode ? '#374151' : '#e0e0e0'}`,
    borderRight: `1px solid ${isDarkMode ? '#374151' : '#e0e0e0'}`,
    
    backgroundColor: isDarkMode ? '#1F2937' : '#f5f5f5',
    fontWeight: '500',
    fontSize: '0.88rem'
  };

  // Sticky table headers: we have 2 header rows (group labels + actual column labels).
  // Keep both visible by stacking with a fixed top offset.
  const groupHeaderRowTopOffsetPx = 30;

  const stickyHeaderCellStyle: React.CSSProperties = {
    ...headerCellStyle,
    position: 'sticky',
    top: groupHeaderRowTopOffsetPx,
    zIndex: 3,
  };

  const stickyHeaderFirstCellStyle: React.CSSProperties = {
    ...stickyHeaderCellStyle,
    left: 0,
    zIndex: 4,
    width: 280,
    minWidth: 280,
  };

  const cellStyle: React.CSSProperties = {
    padding: '1rem',
    textAlign: 'center',
    borderBottom: `1px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`,
    borderRight: `1px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`,
    borderLeft: `1px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`,
  };

  const firstColumnStyle: React.CSSProperties = {
    ...cellStyle,
    textAlign: 'left',
    fontWeight: '500',
    position: 'sticky',
    left: 0,
    width: 280,
    minWidth: 280,
    backgroundColor: isDarkMode ? '#111827' : '#ffffff',
    zIndex: 1,
    borderRight: `1px solid ${isDarkMode ? '#1F2937' : '#e0e0e0'}`
  };

  const sectionHeaderStyle: React.CSSProperties = {
    ...headerCellStyle,
    backgroundColor: isDarkMode ? 'rgba(26, 26, 26, 1)' : '#e8e8e8',
    fontWeight: '700',
    fontSize: '14px'
  };

  const ttmCellStyle: React.CSSProperties = {
    ...cellStyle,
    borderLeft: `3px solid ${isDarkMode ? '#555' : '#999'}`
  };

  const ttmHeaderStyle: React.CSSProperties = {
    ...headerCellStyle,
    borderLeft: `3px solid ${isDarkMode ? '#555' : '#999'}`
  };

  const stickyTtmHeaderStyle: React.CSSProperties = {
    ...ttmHeaderStyle,
    position: 'sticky',
    top: groupHeaderRowTopOffsetPx,
    zIndex: 3,
  };

  const stickyGroupHeaderCellStyle: React.CSSProperties = {
    background: isDarkMode ? '#111827' : '#fff',
    height: `${groupHeaderRowTopOffsetPx}px`,
    fontSize: '14px',
    color: isDarkMode ? '#F9FAFB' : '#0D0D0D',
    fontWeight: 300,
    border: 'none',
    position: 'sticky',
    top: 0,
    zIndex: 5,
  };

  const stickyGroupHeaderLeftCellStyle: React.CSSProperties = {
    ...stickyGroupHeaderCellStyle,
    left: 0,
    zIndex: 6,
  };

  const headerLinkStyle: React.CSSProperties = {
    color: isDarkMode ? '#93C5FD' : '#1D4ED8',
    textDecoration: 'underline',
    cursor: 'pointer',
  };

  const headerLinkButtonStyle: React.CSSProperties = {
    ...headerLinkStyle,
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
  };

  const spacerCellStyle: React.CSSProperties = {
    ...cellStyle,
    padding: '0.25rem',
    borderLeft: 'none',
    borderRight: 'none',
  };

  const spacerFirstColumnStyle: React.CSSProperties = {
    ...firstColumnStyle,
    padding: '0.25rem',
    borderLeft: 'none',
    borderRight: 'none',
  };

  if (loading) {
    return (
      <div id="balance-sheet-container" style={containerStyle}>
        <CenteredLoadingSpinner isDarkMode={isDarkMode} minHeight="240px" />

        <FloatingNewNoteButton
          visible={showFloatingButton}
          onClick={openNewNote}
          label="New Note"
          backgroundColor={isDarkMode ? '#374151' : undefined}
        />

        <ResearchNoteModal
          isOpen={isNoteModalOpen}
          onRequestClose={() => setIsNoteModalOpen(false)}
          isDarkMode={isDarkMode}
          noteTitle={noteTitle}
          onChangeTitle={setNoteTitle}
          noteCategory={noteCategory}
          onChangeCategory={setNoteCategory}
          noteContent={noteContent}
          onChangeContent={setNoteContent}
          categories={NOTE_CATEGORIES}
          canSubmit={canAddNote}
          onSubmit={async () => {
            if (!canPersist) {
              onNotify?.('Please login to save notes.', 'error');
              return;
            }
            const token = authToken as string;
            const userId = authUserId as number;
            const title = extractTitleFromContent(noteContent);
            if (!title) return;
            const associatedWith = noteCategory.trim();
            if (!associatedWith) return;

            try {
              await adminApi.createResearchDeskNote(token, {
                userId,
                title,
                associatedWith,
                stockSymbol: selectedStock,
                html: noteContent ?? '',
              });
              setIsNoteModalOpen(false);
              setNoteTitle('');
              setNoteContent('');
              setNoteCategory('Balance Sheet');
              onNotify?.(`${selectedStock} • Balance Sheet • Saved`, 'success');
            } catch (e: any) {
              if (isUnauthorized(e)) onUnauthorized?.();
            }
          }}
        />
      </div>
    );
  }

  return (
    <div id="balance-sheet-container" style={containerStyle}>
      <div id="balance-sheet-table-wrapper" style={{ ...tableContainerStyle, position: 'relative' }}>
        {showSourceLabels ? <div style={floatingSourceLabelStyle}>{sourceLabelText}</div> : null}
        <table id="balance-sheet-table" style={tableStyle}>
          <thead>
            <tr>
              <th style={stickyGroupHeaderLeftCellStyle}></th>
              <th
                colSpan={annualPeriods.length}
                style={{ ...stickyGroupHeaderCellStyle, textAlign: 'left' }}
              >
                Annuals
              </th>
              <th
                style={{ ...stickyGroupHeaderCellStyle, textAlign: 'center', padding: '5px' }}
              >
                
              </th>
              <th
                colSpan={quarterlyPeriods.length}
                style={{ ...stickyGroupHeaderCellStyle, textAlign: 'left', padding: '1px' }}
              >
                Quarterly
              </th>
            </tr>
            <tr>
              <th style={stickyHeaderFirstCellStyle}>Fiscal Period</th>
              {annualPeriods.map((period, index) => (
                <th
                  key={`annual-${index}`}
                  style={{
                    ...stickyHeaderCellStyle,
                    fontWeight: latestAnnualYear != null && new Date(period.date).getFullYear() === latestAnnualYear ? 800 : stickyHeaderCellStyle.fontWeight,
                  }}
                >
                  {(() => {
                    const label = formatDate(period.date, 'annual');
                    const year = new Date(period.date).getFullYear();
                    const directFilingId = fiscalAnnualFilingIdByLabel[label];
                    if (dataSource !== 'fiscal') return formatDate(period.date, 'annual');
                    return (
                      <button type="button" onClick={() => openAnnualFilingForYear(year, directFilingId)} style={headerLinkButtonStyle}>
                        {formatDate(period.date, 'annual')}
                      </button>
                    );
                  })()}
                </th>
              ))}
              <th style={stickyTtmHeaderStyle}>TTM</th>
              {quarterlyPeriods.map((period, index) => (
                <th
                  key={`quarterly-${index}`}
                  style={{
                    ...stickyHeaderCellStyle,
                    fontWeight: highlightedQuarterDate && period.date === highlightedQuarterDate ? 800 : stickyHeaderCellStyle.fontWeight,
                  }}
                >
                  {(() => {
                    const label = formatDate(period.date, 'quarter');
                    const isAnnualLike = annualDateSet.has(period.date);
                    const directFilingId = isAnnualLike
                      ? fiscalAnnualFilingIdByLabel[label]
                      : fiscalQuarterFilingIdByLabel[label];
                    if (dataSource !== 'fiscal') return formatDate(period.date, 'quarter');
                    return (
                      <button
                        type="button"
                        onClick={() => openQuarterFilingForDate(period.date, directFilingId, isAnnualLike ? '10-K' : '10-Q')}
                        style={headerLinkButtonStyle}
                      >
                        {formatDate(period.date, 'quarter')}
                      </button>
                    );
                  })()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.isSpacer ? (
                  <>
                    <td style={spacerFirstColumnStyle}></td>
                    {annualPeriods.map((_, colIndex) => (
                      <td key={`annual-${colIndex}`} style={spacerCellStyle}></td>
                    ))}
                    <td style={{ ...spacerCellStyle, ...ttmCellStyle }}></td>
                    {quarterlyPeriods.map((_, colIndex) => (
                      <td key={`quarterly-${colIndex}`} style={spacerCellStyle}></td>
                    ))}
                  </>
                ) : (
                  <>
                    <td
                      style={row.isBold ? { ...firstColumnStyle, fontWeight: 700 } : firstColumnStyle}
                    >
                      {row.label}
                    </td>
                    {annualPeriods.map((period, colIndex) => {
                      const text = row.getValue(period);
                      const base = row.isBold ? { ...cellStyle, fontWeight: 700 } : cellStyle;
                      return (
                        <td key={`annual-${colIndex}`} style={withNegativeColor(base, text)}>
                          {text}
                        </td>
                      );
                    })}
                    {(() => {
                      const text = ttmData ? row.getValue(ttmData) : '-';
                      const base = row.isBold ? { ...ttmCellStyle, fontWeight: 700 } : ttmCellStyle;
                      return <td style={withNegativeColor(base, text)}>{text}</td>;
                    })()}
                    {quarterlyPeriods.map((period, colIndex) => {
                      const text = row.getValue(period);
                      const base = row.isBold ? { ...cellStyle, fontWeight: 700 } : cellStyle;
                      return (
                        <td key={`quarterly-${colIndex}`} style={withNegativeColor(base, text)}>
                          {text}
                        </td>
                      );
                    })}
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {dataSource === 'fiscal' ? <DataSourceAttribution isDarkMode={isDarkMode} /> : null}

      <FloatingNewNoteButton
        visible={showFloatingButton}
        onClick={openNewNote}
        label="New Note"
        backgroundColor={isDarkMode ? '#374151' : undefined}
      />

      <ResearchNoteModal
        isOpen={isNoteModalOpen}
        onRequestClose={() => setIsNoteModalOpen(false)}
        isDarkMode={isDarkMode}
        noteTitle={noteTitle}
        onChangeTitle={setNoteTitle}
        noteCategory={noteCategory}
        onChangeCategory={setNoteCategory}
        noteContent={noteContent}
        onChangeContent={setNoteContent}
        categories={NOTE_CATEGORIES}
        canSubmit={canAddNote}
        onSubmit={async () => {
          if (!canPersist) {
            onNotify?.('Please login to save notes.', 'error');
            return;
          }
          const token = authToken as string;
          const userId = authUserId as number;
          const title = extractTitleFromContent(noteContent);
          if (!title) return;
          const associatedWith = noteCategory.trim();
          if (!associatedWith) return;

          try {
            await adminApi.createResearchDeskNote(token, {
              userId,
              title,
              associatedWith,
              stockSymbol: selectedStock,
              html: noteContent ?? '',
            });
            setIsNoteModalOpen(false);
            setNoteTitle('');
            setNoteContent('');
            setNoteCategory('Balance Sheet');
            onNotify?.(`${selectedStock} • Balance Sheet • Saved`, 'success');
          } catch (e: any) {
            if (isUnauthorized(e)) onUnauthorized?.();
          }
        }}
      />
    </div>
  );
};

export default ResearchBalanceSheetScreen;

